import type React from 'react';
import { useEffect, useRef } from 'react';

type UseAudioEventListenerProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	onContentPaused: () => void;
	onContentResumed: () => void;
	onContentEnded: () => void;
};

export const useAudioEventListener = ({
	audioRef,
	onContentPaused,
	onContentResumed,
	onContentEnded,
}: UseAudioEventListenerProps) => {
	const audio = audioRef.current;
	const eventListenersWereAdded = useRef<boolean>(false);

	useEffect(() => {
		if (!audio || eventListenersWereAdded.current) return;
		eventListenersWereAdded.current = true;

		const onPaused = () => {
			onContentPaused();
		};

		const onPlayed = () => {
			onContentResumed();
		};

		const onEnded = () => {
			onContentEnded();
		};

		const onCanPlay = async () => {
			await audio.play();
		};

		audio.addEventListener('pause', onPaused);
		audio.addEventListener('play', onPlayed);
		audio.addEventListener('ended', onEnded);
		audio.addEventListener('canplay', onCanPlay);

		return () => {
			if (audio && eventListenersWereAdded.current) {
				audio.removeEventListener('pause', onPaused);
				audio.removeEventListener('play', onPlayed);
				audio.removeEventListener('ended', onEnded);
				audio.removeEventListener('canplay', onCanPlay);
			}
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [audio]);
};
